import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /**
   * Reinforced confirmation (multi-band person deletion, lot A 2026-10-15): the
   * dialog renders this as a checkbox and stays DISABLED until it is ticked, so
   * the destructive path cannot be walked through on a reflex. Omit it for every
   * ordinary single-target confirm — existing callers are unaffected.
   */
  ack?: string;
}

interface ConfirmRequest {
  message: string;
  opts: ConfirmOptions;
  resolve: (v: boolean) => void;
}

// Module-level confirm bus: a page calls showConfirm(...) → Promise<boolean>,
// and the mounted <ConfirmProvider> renders the styled modal and resolves it.
let listener: ((req: ConfirmRequest) => void) | null = null;

/**
 * Async confirmation. Returns true if the user confirmed, false otherwise.
 * If no <ConfirmProvider> is mounted (shouldn't happen), it resolves false.
 */
export function showConfirm(message: string, opts: ConfirmOptions = {}): Promise<boolean> {
  return new Promise((resolve) => {
    if (!listener) {
      resolve(false);
      return;
    }
    listener({ message, opts, resolve });
  });
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<ConfirmRequest | null>(null);
  const reqRef = useRef<ConfirmRequest | null>(null);
  // Reinforced mode: the "I understand" box is unticked on every new request.
  const [ackChecked, setAckChecked] = useState(false);

  useEffect(() => {
    listener = (r) => {
      reqRef.current = r;
      setAckChecked(false);
      setReq(r);
    };
    return () => {
      listener = null;
    };
  }, []);

  const close = (value: boolean) => {
    if (reqRef.current) reqRef.current.resolve(value);
    reqRef.current = null;
    setReq(null);
  };

  const cancel = useCallback(() => {
    if (reqRef.current) reqRef.current.resolve(false);
    reqRef.current = null;
    setReq(null);
  }, []);
  // Reinforced mode: the confirm button is disabled until the box is ticked, and
  // we refuse here too so Enter/other paths cannot bypass the acknowledgement.
  const accept = () => {
    if (reqRef.current?.opts.ack && !ackChecked) return;
    close(true);
  };

  useEffect(() => {
    if (!req) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [req, cancel]);

  const title = req?.opts.title ?? 'Please confirm';
  const confirmLabel = req?.opts.confirmLabel ?? 'Confirm';
  const cancelLabel = req?.opts.cancelLabel ?? 'Cancel';

  return (
    <>
      {children}
      {req &&
        createPortal(
          <div
            className="modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => {
              if (e.target === e.currentTarget) cancel();
            }}
          >
            <div className="modal">
              <div className="modal-header">
                <h3 className="modal-title">{title}</h3>
                <button type="button" className="modal-close" aria-label="Close dialog" onClick={cancel}>
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <p className="modal-message" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {req.message}
                </p>
                {req.opts.ack && (
                  <label className="modal-ack" data-confirm-ack>
                    <input
                      type="checkbox"
                      checked={ackChecked}
                      onChange={(e) => setAckChecked(e.target.checked)}
                    />
                    <span>{req.opts.ack}</span>
                  </label>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={cancel}>
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  className={req.opts.danger ? 'btn btn-danger' : 'btn btn-primary'}
                  disabled={Boolean(req.opts.ack) && !ackChecked}
                  onClick={accept}
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
